import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "../integrations/supabase/client";
import { Session, User, AuthError } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { Json } from "../types/database.types";

export type UserRole = "customer" | "florist" | "admin";

interface AuthUser extends User {
  user_metadata: {
    full_name?: string;
    role?: UserRole;
    name?: string; // from Google
  };
}

// Local interface for customer profile inserts
interface CustomerProfileInsert {
  id: string;
  full_name: string;
  email: string;
  suburb: string;
  state: string;
  postcode: string;
  phone?: string | null;
  unit_number?: string | null;
  street_number?: string | null;
  street_name?: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  isFlorist: boolean;
  isAdmin: boolean;
  isCustomer: boolean;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    role: UserRole
  ) => Promise<{
    data: { user: User | null; session: Session | null } | null;
    error: AuthError | { message: string } | null;
  }>;
  signIn: (email: string, password: string) => Promise<{
    data: { user: User | null; session: Session | null } | null;
    error: AuthError | null;
  }>;
  signOut: () => Promise<void>;
  verifyAuth: () => Promise<boolean>;
  supabase: typeof supabase;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirected, setRedirected] = useState(false); // New state to track redirection
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
        setUser(initialSession?.user as AuthUser ?? null);
        setSession(initialSession);
        setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, currentSession) => {
      setUser(currentSession?.user as AuthUser ?? null);
      setSession(currentSession);
      setLoading(false);
      
      if (_event === 'SIGNED_OUT') {
        setRedirected(false); // Reset on sign-out
        return;
      }
      
      // Only handle profile creation for new social login users
      // Remove auto-redirects that were causing navigation issues
      if (_event === 'SIGNED_IN' && currentSession?.user && !redirected) {
        setRedirected(true); // Set flag to prevent future redirects
        const authUser = currentSession.user as AuthUser;
        const userRole = authUser.user_metadata?.role || 'customer';
        
        // Only create profile for new customer social logins
        if (userRole === 'customer' && authUser.app_metadata.provider !== 'email') {
          // Check for and create a customer profile if it's their first time (social login)
          const { error: profileError } = await (supabase.from('customer_profiles') as any)
            .select('id')
            .eq('id', authUser.id)
            .single();

          if (profileError) { // "No rows found" is an error, indicating new user
            const fullName = authUser.user_metadata.full_name || authUser.user_metadata.name || 'New User';
            
            const newProfile: CustomerProfileInsert = {
              id: authUser.id,
              full_name: fullName,
              email: authUser.email!,
              suburb: "Not specified",
              state: "NSW",
              postcode: "2000",
              phone: null,
              unit_number: null,
              street_number: null,
              street_name: null,
            };

            await (supabase.from('customer_profiles') as any).insert(newProfile);

            const welcomeEmailPayload = {
              type: 'WELCOME_EMAIL',
              email: authUser.email!,
              data: { customerName: fullName, isNewCustomerFromGoogle: true }
            };
            supabase.functions.invoke('send-brevo-notification', { body: welcomeEmailPayload });
          }
        }
        // Removed all auto-redirects - let users stay on current page
      }
    });

    return () => { subscription.unsubscribe(); };
  }, [navigate, redirected]);

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    role: UserRole,
    extraData?: any
  ): Promise<{
    data: { user: User | null; session: Session | null; } | null;
    error: AuthError | { message: string } | null;
  }> => {
    setLoading(true);
    try {
      // ✅ FIXED: Removed customer-only restriction to enable florist signups

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName, role: role } },
      });

      if (authError) throw authError;
      if (!authData.user || !authData.session) throw new Error("User or session not returned from Supabase signUp");

      if (role === 'florist') {
        // ✅ FIXED: Minimal store profile creation to avoid constraint issues
        console.log("Creating store profile for florist:", authData.user.id);
        
        const storeProfileToInsert = {
          id: authData.user.id,
          florist_id: authData.user.id,
          store_name: extraData?.store_name || `${fullName}'s Flower Shop`,
          email_notification_preferences: false // ✅ AUTO-DISABLED emails for new florists
        };

        console.log("Store profile data to insert:", storeProfileToInsert);
        const { error: storeInsertError } = await supabase.from('store_profiles').insert(storeProfileToInsert);
        
        if (storeInsertError) {
          console.error("AuthContext: CRITICAL - Florist user created but store profile insert failed:", storeInsertError);
          toast.error("Account created, but we couldn't set up your store profile. Please contact support immediately.");
          return { data: { user: authData.user, session: authData.session }, error: { message: `Store profile creation failed: ${storeInsertError.message}` } };
        }

        toast.success("Florist account created successfully! Please check your email to verify.");
        return { data: { user: authData.user, session: authData.session }, error: null };
      } else {
        // Customer signup logic
        const [firstName, ...lastNameParts] = fullName.split(' ');
        const lastName = lastNameParts.join(' ') || ' ';

        const profileToInsert: CustomerProfileInsert = {
          id: authData.user.id,
          full_name: fullName,
          email: email,
          suburb: extraData?.suburb || "Not specified",
          state: extraData?.state || "NSW",
          postcode: extraData?.postcode || "2000",
          phone: extraData?.phone || null,
          unit_number: extraData?.unit_number || null,
          street_number: extraData?.street_number || null,
          street_name: extraData?.street_name || null,
        };

        const { error: insertError } = await (supabase.from('customer_profiles') as any).insert(profileToInsert);

        if (insertError) {
          console.error("AuthContext: CRITICAL - User created but profile insert failed:", insertError);
          toast.error("Account created, but we couldn't set up your profile. Please contact support immediately.");
          return { data: { user: authData.user, session: authData.session }, error: { message: `Profile creation failed: ${insertError.message}` } };
        }

        const welcomeEmailPayload = {
          type: 'WELCOME_EMAIL',
          email: authData.user.email!,
          data: { customerName: fullName, isNewCustomerFromGoogle: false }
        };
        await supabase.functions.invoke('send-brevo-notification', { body: welcomeEmailPayload });
        
        toast.success("Account created successfully! Please check your email to verify.");

        return { data: { user: authData.user, session: authData.session }, error: null };
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to create account.");
      return { data: null, error: error as AuthError | { message: string } };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (!data.user || !data.session) throw new Error("Sign in failed: No user or session returned.");
      return { data, error: null };
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in.");
      return { data: null, error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setRedirected(false); // Reset the redirect flag on sign out
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || "Failed to sign out.");
    } finally {
      setLoading(false);
    }
  };
  
  const verifyAuth = async (): Promise<boolean> => {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) {
      return false;
    }
    return true;
  };

  const value = {
    user,
    session,
    loading,
    isFlorist: user?.user_metadata?.role === "florist",
    isAdmin: user?.user_metadata?.role === "admin",
    isCustomer: user?.user_metadata?.role === "customer",
    signUp,
    signIn,
    signOut,
    verifyAuth,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}